import PropTypes from "prop-types";
import styled from "styled-components";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { useWindowSize } from "../../../../services/hooks";

const props = {
    originImage: PropTypes.string,
    qrcode: PropTypes.string,
    setQrcodeLoaded: PropTypes.func,
    topText: PropTypes.string,
    bottomText: PropTypes.string,
    disabled: PropTypes.bool,
    setImageReady: PropTypes.func,
};

const DrawBoard = forwardRef(
    ({ originImage, layout, qrcode, topText, bottomText, disabled, setImageReady, setQrcodeLoaded }, ref) => {
        const wrapperRef = useRef(null);
        const canvasRef = useRef(null);
        const contextRef = useRef(null);

        const { width: winWidth } = useWindowSize();

        const [isDrawing, setIsDrawing] = useState(false);
        const [drawn, setDrawn] = useState(false);
        const [width, setWidth] = useState(0);

        useImperativeHandle(ref, () => ({
            clearCanvas() {
                clearCanvasHandler();
            },
            insertImage(imageUrl) {
                insertImageHandler(imageUrl);
            },
            insertText(text) {
                insertTextHandler(text);
            },
            getSignatureImageBase64() {
                return drawn ? canvasRef.current.toDataURL() : "";
            },
        }));

        useEffect(() => {
            if (wrapperRef.current) {
                setWidth(wrapperRef.current.offsetWidth);
            }
        }, [winWidth]);

        useEffect(() => {
            const canvas = canvasRef.current;
            const context = canvas.getContext("2d");
            context.scale(1, 1);
            context.lineCap = "round";
            context.strokeStyle = "rgb(29, 56, 163)";
            context.lineWidth = 2;
            contextRef.current = context;
            clearCanvasHandler();
            if (originImage) insertImageHandler(originImage);
        }, []); // eslint-disable-line

        useEffect(() => {
            const canvas = canvasRef.current;
            canvas.width = width - 2;
            canvas.height = Math.ceil(width / 3);
            canvas.style.width = `${width - 2}px`;
            canvas.style.height = `${Math.ceil(width / 3)}px`;
            clearCanvasHandler();
            if (originImage) insertImageHandler(originImage);
        }, [width]); // eslint-disable-line

        const startDrawing = ({ nativeEvent }) => {
            const { offsetX, offsetY } = nativeEvent;
            contextRef.current.beginPath();
            contextRef.current.moveTo(offsetX, offsetY);
            setIsDrawing(true);
            setDrawn(true);
            if (setImageReady && !disabled) setImageReady(true);
        };

        const finishDrawing = () => {
            contextRef.current.closePath();
            setIsDrawing(false);
        };

        const draw = ({ nativeEvent }) => {
            if (!isDrawing || disabled) return;
            const { offsetX, offsetY } = nativeEvent;
            contextRef.current.lineTo(offsetX, offsetY);
            contextRef.current.stroke();
        };

        const startTouching = (e) => {
            const touchEvent = e.touches[0];
            if (touchEvent && canvasRef.current) {
                let rect = canvasRef.current.getBoundingClientRect();
                const offsetX = touchEvent.clientX - rect.x;
                const offsetY = touchEvent.clientY - rect.y;
                contextRef.current.beginPath();
                contextRef.current.moveTo(offsetX, offsetY);
                setIsDrawing(true);
                setDrawn(true);
                if (setImageReady && !disabled) setImageReady(true);
            }
        };

        const finishTouching = () => {
            contextRef.current.closePath();
            setIsDrawing(false);
        };

        const touch = (e) => {
            if (!isDrawing || disabled) return;
            const touchEvent = e.touches[0];
            if (touchEvent && canvasRef.current) {
                let rect = canvasRef.current.getBoundingClientRect();
                const offsetX = touchEvent.clientX - rect.x;
                const offsetY = touchEvent.clientY - rect.y;
                contextRef.current.lineTo(offsetX, offsetY);
                contextRef.current.stroke();
            }
        };

        const clearCanvasHandler = () => {
            const canvas = canvasRef.current;
            const context = canvas.getContext("2d");
            context.fillStyle = "#FFFFFF";
            context.fillRect(0, 0, canvas.width, canvas.height);
            setDrawn(false);
            if (setImageReady) setImageReady(false);

            context.beginPath();
            context.lineWidth = "1";
            context.strokeStyle = "rgb(129,129,129)";
            context.rect(1, 1, canvas.width - 2, canvas.height - 2);
            context.stroke();

            if (qrcode) {
                let img = new Image();
                img.crossOrigin = "anonymous";
                img.onload = function () {
                    const qrCodeWidth = Math.round(canvas.height - (canvas.width > 590 ? 80 : 45));
                    console.log(qrCodeWidth, width);
                    context.drawImage(
                        img,
                        canvas.width - qrCodeWidth - 10,
                        canvas.height / 2 - Math.round(qrCodeWidth / 2),
                        qrCodeWidth,
                        qrCodeWidth
                    );
                    if (setQrcodeLoaded) setQrcodeLoaded(true);
                };
                img.src = qrcode;
            }
            if (topText) {
                context.font = width === 600 ? "18px sans-serif" : "13px sans-serif";
                context.fillStyle = "#323232";
                context.textAlign = "center";
                context.fillText(`${topText}`, canvas.width / 2, width === 600 ? 25 : 17);
            }
            if (bottomText) {
                context.font = width === 600 ? "18px sans-serif" : "13px sans-serif";
                context.fillStyle = "#323232";
                context.textAlign = "center";
                // context.fillText(`${GUID}`, canvas.width/2, 30);
                context.fillText(
                    `${bottomText}`,
                    canvas.width / 2,
                    width === 600 ? canvas.height - 12 : canvas.height - 8
                );
            }
            context.lineWidth = "1";
            context.strokeStyle = "rgb(29, 56, 163)";
        };

        const insertImageHandler = (imageUrl) => {
            const canvas = canvasRef.current;
            const context = canvas.getContext("2d");
            let image = new Image(canvas.width, canvas.height);
            image.crossOrigin = "anonymous";
            image.src = imageUrl;
            image.onload = function () {
                image.width = canvas.width;
                image.height = canvas.height;
                if (layout === "remote") {
                    context.drawImage(image, 10, 40, canvas.width - 155, canvas.height - 80);
                } else {
                    context.drawImage(image, 0, 0, canvas.width, canvas.height);
                }
                setDrawn(true);
                if (setImageReady) setImageReady(true);
            };
        };

        const insertTextHandler = (text) => {
            clearCanvasHandler();

            const canvas = canvasRef.current;
            const context = canvas.getContext("2d");

            context.fillStyle = "#000099";
            context.textAlign = "center";
            context.font = canvas.width > 590 ? "70px Qwitcher Grypen" : "40px Qwitcher Grypen";

            if (text.length < 10) {
                if (canvas.width > 590) {
                    context.fillText(text, canvas.width / 2 - 60, canvas.height / 2);
                } else {
                    context.fillText(text, canvas.width / 2 - 45, canvas.height / 2);
                }
            } else {
                const textList = text.split(" ");
                let text1 = "";
                let text2 = "";
                let addToText1 = true;
                textList.forEach((item) => {
                    if (`${text1} ${item}`.length <= 12 && addToText1) {
                        text1 += ` ${item}`;
                        addToText1 = false;
                    } else text2 += ` ${item}`;
                });

                if (canvas.width > 590) {
                    context.fillText(text1, canvas.width / 2 - 60, text2 ? canvas.height / 2 - 15 : canvas.height / 2);
                    if (text2) context.fillText(text2, canvas.width / 2 - 60, canvas.height / 2 + 30);
                } else {
                    context.fillText(text1, canvas.width / 2 - 45, text2 ? canvas.height / 2 - 8 : canvas.height / 2);
                    if (text2) context.fillText(text2, canvas.width / 2 - 45, canvas.height / 2 + 16);
                }
            }

            if (text.trim()) {
                setDrawn(true);
                if (setImageReady) setImageReady(true);
            }
        };

        return (
            <Wrapper ref={wrapperRef} disabled={disabled}>
                <canvas
                    id="signature_image"
                    onMouseDown={startDrawing}
                    onMouseUp={finishDrawing}
                    onMouseMove={draw}
                    onTouchStart={startTouching}
                    onTouchEnd={finishTouching}
                    onTouchMove={touch}
                    ref={canvasRef}
                />
            </Wrapper>
        );
    }
);

const Wrapper = styled.div`
    cursor: ${(props) => (props.disabled ? "not-allowed" : "crosshair")};

    canvas {
        touch-action: none;
    }
`;

DrawBoard.propTypes = props;

export default DrawBoard;
